import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Switch, Chip, Typography, CircularProgress, Alert, InputAdornment,
  IconButton, Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, FormGroup, FormControlLabel, Checkbox,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BlockIcon from '@mui/icons-material/Block';
import TopNavbar from '../../components/TopNavBar/TopNavBar';
import AdminPortalSideBar from '../../containers/AdminSideBarContainer/AdminSideBarContainer';
import { PageContainer, ContentContainer } from '../AdminPortalPage/AdminPortalPage.styles';
import {
  getAllUsers, updateUser, createTeamMember, updateUserRoles, getRoles,
  deactivateUser, activateUser,
} from '../../api/api';
import { useToast } from '../../components/ToastProvider/ToastProvider';

const INITIAL_FORM = { full_name: '', email: '', password: '', roles: [] };

export default function UserManagementPage() {
  const { showToast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [updatingUsers, setUpdatingUsers] = useState(new Set());

  const [allRoles, setAllRoles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [form, setForm] = useState(INITIAL_FORM);
  const [editUserId, setEditUserId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllUsers({ limit: 10000 });
      const allUsers = data.users || data || [];

      const members = allUsers.filter(u => {
        const roles = u.roles || [];
        return roles.length > 0 || u.is_admin;
      });

      members.sort((a, b) => {
        const nameA = (a.full_name || a.username || '').toLowerCase();
        const nameB = (b.full_name || b.username || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setUsers(members);
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
      showToast('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const fetchRoles = useCallback(async () => {
    try {
      const roles = await getRoles();
      setAllRoles(roles || []);
    } catch {
      setAllRoles([]);
    }
  }, []);

  useEffect(() => { fetchUsers(); fetchRoles(); }, [fetchUsers, fetchRoles]);

  const filtered = users.filter(u => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      (u.email || '').toLowerCase().includes(term) ||
      (u.username || '').toLowerCase().includes(term) ||
      (u.full_name || '').toLowerCase().includes(term);

    const matchesRole = roleFilter === 'all' ||
      (roleFilter === 'admin' && u.is_admin) ||
      (u.roles || []).includes(roleFilter);

    return matchesSearch && matchesRole;
  });

  const isAdminSelected = form.roles.includes('admin');

  const handleToggleAdmin = async (userId, currentIsAdmin) => {
    if (updatingUsers.has(userId)) return;
    setUpdatingUsers(prev => new Set(prev).add(userId));
    try {
      const newIsAdmin = !currentIsAdmin;
      await updateUser(userId, { is_admin: newIsAdmin });
      setUsers(prev => prev.map(user =>
        user.id === userId ? { ...user, is_admin: newIsAdmin } : user
      ));
      showToast(`User ${newIsAdmin ? 'promoted to' : 'removed from'} admin successfully`, 'success');
    } catch (err) {
      showToast(err.message || 'Failed to update user', 'error');
    } finally {
      setUpdatingUsers(prev => { const s = new Set(prev); s.delete(userId); return s; });
    }
  };

  const openAddDialog = () => {
    setDialogMode('add');
    setForm(INITIAL_FORM);
    setEditUserId(null);
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setDialogMode('edit');
    setForm({
      full_name: user.full_name || user.username || '',
      email: user.email || '',
      password: '',
      roles: user.roles || [],
    });
    setEditUserId(user.id);
    setDialogOpen(true);
  };

  const handleRoleToggle = (roleName) => {
    setForm(f => {
      const current = f.roles;
      if (roleName === 'admin') {
        return { ...f, roles: current.includes('admin') ? [] : ['admin'] };
      }
      if (current.includes('admin')) return f;
      const next = current.includes(roleName)
        ? current.filter(r => r !== roleName)
        : [...current, roleName];
      return { ...f, roles: next };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (dialogMode === 'add') {
        if (!form.full_name.trim() || !form.email.trim() || !form.password) {
          showToast('Name, email, and password are required', 'error');
          setSaving(false);
          return;
        }
        await createTeamMember({
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          password: form.password,
          roles: form.roles,
        });
        showToast('User created successfully', 'success');
      } else {
        await updateUserRoles(editUserId, form.roles);
        if (form.email.trim()) {
          const currentUser = users.find(u => u.id === editUserId);
          if (currentUser && currentUser.email !== form.email.trim()) {
            await updateUser(editUserId, { email: form.email.trim() });
          }
        }
        showToast('User updated successfully', 'success');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      showToast(err.message || 'Operation failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    try {
      await deactivateUser(deactivateTarget.id);
      setUsers(prev => prev.map(u =>
        u.id === deactivateTarget.id ? { ...u, is_active: false } : u
      ));
      showToast(`${deactivateTarget.full_name || deactivateTarget.email} has been deactivated`, 'success');
      setDeactivateDialogOpen(false);
      setDeactivateTarget(null);
    } catch (err) {
      showToast(err.message || 'Failed to deactivate user', 'error');
    } finally {
      setDeactivating(false);
    }
  };

  const uniqueRoleNames = [...new Set(users.flatMap(u => u.roles || []))].sort();

  return (
    <>
      <TopNavbar />
      <PageContainer>
        <AdminPortalSideBar />
        <ContentContainer>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>User Management</Typography>
            <Button variant="contained" startIcon={<PersonAddIcon />} onClick={openAddDialog} sx={{ textTransform: 'none' }}>
              Add User
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small" placeholder="Search by name or email..."
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              sx={{ width: 300 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Filter by Role</InputLabel>
              <Select value={roleFilter} label="Filter by Role" onChange={e => setRoleFilter(e.target.value)}>
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                {uniqueRoleNames.filter(r => r !== 'admin').map(r => (
                  <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r.replace(/_/g, ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Total: {filtered.length} user{filtered.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Roles</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Admin</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">No users found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : filtered.map(user => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.full_name || user.username || '—'}</TableCell>
                      <TableCell>{user.email || '—'}</TableCell>
                      <TableCell align="center">
                        {(user.roles || []).length > 0
                          ? (user.roles || []).map(r => (
                              <Chip key={r} label={r.replace(/_/g, ' ')} size="small" sx={{ mr: 0.5, mb: 0.5, textTransform: 'capitalize' }} />
                            ))
                          : user.is_admin ? <Chip label="Admin" size="small" color="primary" /> : '—'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={user.is_active !== false ? 'Active' : 'Inactive'}
                          color={user.is_active !== false ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={user.is_admin || false}
                          onChange={() => handleToggleAdmin(user.id, user.is_admin || false)}
                          disabled={updatingUsers.has(user.id)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit User">
                          <IconButton size="small" onClick={() => openEditDialog(user)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {user.is_active !== false && !user.is_admin && (
                          <Tooltip title="Deactivate User">
                            <IconButton
                              size="small" color="error"
                              onClick={() => { setDeactivateTarget(user); setDeactivateDialogOpen(true); }}
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {user.is_active === false && (
                          <Tooltip title="Activate User">
                            <IconButton
                              size="small" color="success"
                              disabled={updatingUsers.has(user.id)}
                              onClick={async () => {
                                setUpdatingUsers(prev => new Set(prev).add(user.id));
                                try {
                                  await activateUser(user.id);
                                  setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: true } : u));
                                  showToast('User activated', 'success');
                                } catch (err) { showToast(err.message || 'Failed', 'error'); }
                                finally { setUpdatingUsers(prev => { const s = new Set(prev); s.delete(user.id); return s; }); }
                              }}
                            >
                              <BlockIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </ContentContainer>
      </PageContainer>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {dialogMode === 'add' ? 'Add User' : 'Edit User'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Full Name" fullWidth size="small"
              value={form.full_name}
              onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
              disabled={dialogMode === 'edit'}
            />
            <TextField
              label="Email" fullWidth size="small" type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            />
            {dialogMode === 'add' && (
              <TextField
                label="Password" fullWidth size="small" type="password"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                helperText="Minimum 8 characters"
              />
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Roles</Typography>
              {isAdminSelected && (
                <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                  Admin role grants full access — other roles are not needed.
                </Alert>
              )}
              <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 250, overflow: 'auto' }}>
                <FormGroup>
                  {allRoles.map(role => (
                    <FormControlLabel
                      key={role.name}
                      control={
                        <Checkbox
                          checked={form.roles.includes(role.name)}
                          onChange={() => handleRoleToggle(role.name)}
                          disabled={isAdminSelected && role.name !== 'admin'}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: form.roles.includes(role.name) ? 600 : 400 }}>
                            {role.name.replace(/_/g, ' ')}
                          </Typography>
                          {role.description && (
                            <Typography variant="caption" color="text.secondary">{role.description}</Typography>
                          )}
                        </Box>
                      }
                      sx={{
                        alignItems: 'flex-start',
                        mb: 0.5,
                        opacity: isAdminSelected && role.name !== 'admin' ? 0.5 : 1,
                      }}
                    />
                  ))}
                </FormGroup>
              </Paper>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : dialogMode === 'add' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Confirmation */}
      <Dialog open={deactivateDialogOpen} onClose={() => setDeactivateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Deactivate User</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Are you sure you want to deactivate <strong>{deactivateTarget?.full_name || deactivateTarget?.email}</strong>?
            They will no longer be able to log in until reactivated.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeactivate} disabled={deactivating}>
            {deactivating ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
